<template>
  <div v-loading="loading">
    <div class="creatives-modal rounded overflow-hidden text-dark bg-white flex justify-start items-start">
      <!-- Left: Thumbnails -->
      <div class="w-[88px] h-[680px] flex flex-col justify-between items-center gap-1 border-r border-grayNeutralLight pt-2">
        <el-scrollbar class="h-[576px] w-full mr-0" wrap-style="overflow: auto;">
          <div class="flex flex-col justify-start items-center gap-1">
            <div
              v-for="(file, index) in files"
              :key="file.id"
              :class="`rounded-md ${
                index === currentSlide ? 'border-2 border-primary' : 'border border-white'
              } overflow-hidden cursor-pointer shrink-0`"
              @click="currentSlide = index"
            >
              <img
                :class="{ 'opacity-40': isReplacingFileId === file.id, 'border-2 border-white': index === currentSlide }"
                :src="imageSignature(file.thumbnail_url || file.url)"
                :alt="file.name"
                class="w-[48px] h-[48px] object-cover"
              />
            </div>
          </div>
        </el-scrollbar>
        <button
          v-if="$is_GD"
          :disabled="!canUploadReplaceAndDeleteFile(creative?.status)"
          @click="openUploadModal"
          :class="[
            'w-[40px] h-[40px] rounded-[6px] flex justify-center items-center mb-3',
            { 'opacity-50 cursor-not-allowed': !canUploadReplaceAndDeleteFile(creative?.status) },
          ]"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M6 1V11M1 6H11" stroke="black" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
      <!-- Center: Preview -->
      <div class="w-[812px] h-[680px] flex flex-col justify-between items-center gap-1 p-3">
        <div class="flex justify-center items-center h-full w-full max-h-[580px]">
          <template v-if="files.length">
            <img
              v-if="currentFile?.file_type === 'image'"
              :class="{ 'opacity-40': isReplacingFileId === currentFile.id }"
              class="rounded-[6px] max-h-full object-contain"
              :src="imageSignature(currentFile.url)"
              :alt="currentFile?.name"
            />
            <VideoPlayer
              v-else-if="currentFile?.file_type === 'video'"
              :class="{ 'opacity-40': isReplacingFileId === currentFile.id }"
              :src="imageSignature(currentFile.url)"
            />
          </template>

          <template v-else>
            <div class="flex flex-col justify-center items-center text-center text-sm text-grayNeutralDark px-6">
              <svg class="mb-2 text-gray" width="32" height="32" fill="none">
                <use xlink:href="#img-placeholder" />
              </svg>
              <p class="font-semibold text-base mb-1">No files available in this creative.</p>
            </div>
          </template>
        </div>
        <div v-if="files.length" class="w-full flex items-center gap-1">
          <svg class="text-gray" width="16" height="16" fill="none">
            <use :xlink:href="`#img-placeholder`"></use>
          </svg>
          <span class="text-12px text-gray">
            <span class="max-w-[270px] inline-block truncate align-middle">{{ currentFile?.name }}</span
            ><span class="dot"></span> File size: {{ formattedSize }}
            <span v-if="currentFile?.width && currentFile?.height">
              <span class="dot"></span> Dimensions: {{ currentFile?.width }} by {{ currentFile?.height }} pixels
            </span>
          </span>
          <div class="flex justify-center items-center gap-0">
            <ReplaceFile
              :creative="creative"
              :creativeId="creative.id"
              :fileId="currentFile.id"
              @replaced="init"
              @replacing="handleReplacing"
              :flightStatus="meta.flightStatus"
            />

            <ElButton
              v-if="$is_GD"
              :disabled="!canUploadReplaceAndDeleteFile(creative?.status)"
              @click="deleteFile"
              type="text"
              :class="[
                'w-[40px] h-[40px] flex justify-center items-center',
                { 'is-disabled opacity-50': !canUploadReplaceAndDeleteFile(creative?.status) },
              ]"
            >
              <svg class="text-gray" width="12" height="12" fill="none">
                <use :xlink:href="`#delete-outline`"></use>
              </svg>
            </ElButton>
          </div>
        </div>
      </div>
      <!-- Right: Metadata -->
      <div class="w-[300px] h-[680px] border-l border-grayNeutralLight p-3">
        <CreativeInfo
          :creative="creative"
          @delete="deleteCreative"
          @send-to-review="sendToReview"
          @approve="approve"
          @request-revision="handleRequestRevision"
          :flightStatus="meta.flightStatus"
          :openChat="meta.openChat"
        />
      </div>
    </div>
  </div>
</template>

<script>
import UploadModal from './Upload.vue'
import CreativeInfo from './CreativeInfo.vue'
import DeleteCreative from './DeleteCreative.vue'
import DeleteFile from './DeleteFile.vue'
import ReplaceFile from './ReplaceFile.vue'
import VideoPlayer from '@/components/social-products/social-boost/parts/VideoPlayer'
import CreativesMixin from '@/mixins/social-products/Creatives.js'

export default {
  name: 'CreativeModal',
  mixins: [CreativesMixin],
  components: {
    CreativeInfo,
    VideoPlayer,
    ReplaceFile,
  },
  props: {
    meta: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      creative: null,
      files: [],
      currentSlide: 0,
      loading: false,
      parent: null,
      isReplacingFileId: null,
    }
  },
  computed: {
    currentFile() {
      return this.files[this.currentSlide] || {}
    },
    formattedSize() {
      const size = this.currentFile?.size
      if (!size || isNaN(size)) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(size) / Math.log(1024))
      return (size / Math.pow(1024, i)).toFixed(1) + ' ' + units[i]
    },
    sendToReview() {
      return this.meta?.sendToReview || (() => {})
    },
    approve() {
      return this.meta?.approve || (() => {})
    },
    flightStatus() {
      return this.meta?.flightStatus
    },
  },
  created() {
    this.parent = this.$Modal.parent
    this.init()
    this.$Modal.instance?.$on('layout:change', ({ value }) => {
      if (value === 'next') {
        this.currentSlide = (this.currentSlide + 1) % this.files.length
      } else {
        this.currentSlide = (this.currentSlide - 1 + this.files.length) % this.files.length
      }
    })
  },
  methods: {
    async init() {
      this.loading = true
      const { creative_id } = this.meta || {}
      if (!creative_id) return

      const creative = await this.$store.dispatch('socialBoostCampaign/getCreative', { creative_id })
      this.creative = creative
      this.files = creative?.files || []
      this.loading = false
    },
    close() {
      this.$Modal.instance.isOpen = false
    },
    imageSignature(img) {
      return this.signature(img, 'socialproducts', 1)
    },
    handleReplacing(isReplacing) {
      this.isReplacingFileId = isReplacing ? this.currentFile.id : null
    },
    openUploadModal() {
      this.$Modal.init(
        {
          propsData: {
            component: UploadModal,
            meta: {
              creative_id: this.creative.id,
            },
            size: '460px',
          },
          callback: (payload, $this) => {
            if (payload.event === 'modal:close') {
              $this.$nextTick(() => {
                if (typeof this.parent.openCreativeModal === 'function') {
                  this.parent.openCreativeModal()
                }
              })
            }
          },
        },
        this
      )
    },
    deleteCreative() {
      this.$Modal.init(
        {
          propsData: {
            component: DeleteCreative,
            meta: { creative: this.creative },
            size: '480px',
          },
          callback: (payload) => {
            if (payload.event === 'modal:close') {
            }
          },
        },
        this
      )
    },
    deleteFile() {
      this.$Modal.init(
        {
          propsData: {
            component: DeleteFile,
            meta: { file: this.currentFile },
            size: '480px',
          },
          callback: (payload, $this) => {
            if (payload.event === 'modal:close') {
              $this.$nextTick(() => {
                if (typeof this.parent.openCreativeModal === 'function') {
                  this.parent.openCreativeModal()
                }
              })
            }
          },
        },
        this
      )
    },
    async handleRequestRevision({ creative_id, instructions }) {
      this.loading = true
      await this.$store.dispatch('socialBoostCampaign/requestCreativeRevision', {
        creative_id,
        instructions,
      })
      this.$Modal.bus.$emit('reload:creatives')
      this.close()
      this.loading = false
    },
  },
}
</script>
<style lang="scss" scoped>
.dot {
  @apply w-[4px] h-[4px] bg-bordersDark rounded-full inline-block my-[2px] mx-[8px];
}
::v-deep(.el-scrollbar__wrap) {
  margin-right: 0 !important;
}
</style>
